import * as React from 'react'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {Hub} from 'yougile-model'
import {sharedBoardsEvents} from '@client/shared-boards-events'
import {Sticker} from '@components/common/base/sticker/sticker'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {useForceUpdate, useSyncEvent} from '@containers/hooks'
import {withUpdaters} from '@containers/updaters'
import {ListenerUpdater} from '@containers/updaters/listener-updater'
import {SyncUpdater} from '@containers/updaters/sync-updater'
import {ChatUtils} from '@extensions/chat-utils'
import {Task} from '@extensions/task'

interface Props {
  task: Hub
  showZeroStats?: boolean
}

const getCountMessages = (task: Hub): number => {
  const messages = Array.from(task.chat?.messages?.values() || [])

  return messages.reduce((acc, item) => {
    if (item.deleted) {
      return acc
    }

    if (item.properties?.fromSystem) {
      return acc
    }

    return acc + 1
  }, 0)
}

const ShareTaskStatsBase = (props: Props) => {
  const {task, showZeroStats = true} = props
  const bem = Util.bemFrom('share-task-stats')
  const forceUpdate = useForceUpdate()
  const isTaskColored = Task.isTaskColored(task)

  useSyncEvent(
    'current',
    {filter: task.id},
    () => {
      forceUpdate()
    },
    []
  )

  const total = getCountMessages(task)
  const unseen = ChatUtils.newMessagesFromChat(task)

  if (!showZeroStats && !total && !unseen) {
    return <></>
  }

  return (
    <Tooltip
      content={Lang.msg.share.taskStatsHint(unseen, total)}
      placement="bottom"
    >
      <div className={bem()}>
        <Sticker
          icon="IconCustomMessage"
          color={isTaskColored ? 'sticker-transparent' : 'sticker-green'}
          iconColor={
            isTaskColored ? 'icon-sticker-transparent' : 'icon-sticker-green'
          }
          iconBackgroundColor={
            isTaskColored ? 'icon-sticker-transparent' : 'icon-sticker-green'
          }
          className={`!cursor-default ${
            isTaskColored
              ? `outline-1 ${
                  Task.isDone(task)
                    ? 'opacity-[.38] hover:opacity-[.64]'
                    : 'opacity-[.64] hover:opacity-100'
                }`
              : undefined
          }`}
          title={
            <ShareTaskTitle
              total={total}
              unseen={unseen}
              isTaskColored={isTaskColored}
            />
          }
        />
      </div>
    </Tooltip>
  )
}

const ShareTaskTitle = ({
  unseen,
  total,
  isTaskColored
}: {
  unseen: number
  total: number
  isTaskColored?: boolean
}) => {
  return (
    <div
      className={`${
        isTaskColored ? 'text-sticker-transparent' : 'text-sticker-gray'
      } w-fit px-1`}
    >
      {Boolean(unseen) && (
        <span className="text-sticker-red">
          {unseen}
          <span className="text-sticker-gray">/</span>
        </span>
      )}
      {total}
    </div>
  )
}

export const ShareTaskStats = withUpdaters(
  ListenerUpdater(sharedBoardsEvents),
  SyncUpdater({filter: '*', debounceTime: 2000, companyType: 'current'})
)((props: Props) => {
  return <ShareTaskStatsBase {...props} />
})
